import { showCookieConsent } from '@lib/cookie';
import { useRouter } from 'next/router';
import { PropsWithChildren, useMemo } from 'react';
import { DEFAULT_LOCALE, getWithoutLocale } from 'utils';
import { RouterContext, RouterContextType } from './RouterContext';

/**
 * Uses next/router as router, can be used only within the pages directory
 * Can be removed when website is migrated completely to app directory.
 * */
export const PagesRouterProvider = ({ children }: PropsWithChildren<{}>) => {
  const router = useRouter();
  const { query } = router;

  const contextValue: RouterContextType = useMemo(() => {
    const currentLocale = (query.locale as string) || DEFAULT_LOCALE;
    let currentPathWithoutLocale = '';
    let currentPath = '';
    if (typeof window !== 'undefined') {
      currentPath = window.location.pathname;
      currentPathWithoutLocale = getWithoutLocale(window.location.pathname);
    }
    return {
      locale: currentLocale,
      changeLocale: (newLocale: string) => {
        const targetPath = `/${newLocale}${currentPathWithoutLocale}`;
        /**
         * Match current path vs our intended target to avoid an error
         * when forcing a router push to the same path the user is already on.
         */
        if (targetPath !== currentPath) {
          router.push({
            pathname: targetPath,
          });
        }
        router.events.on('routeChangeComplete', () => {
          router.reload();
        });

        /**
         * If Cookie Consent is enabled, we need to update the language in OneTrust as well.
         */
        showCookieConsent() &&
          router.events.on('routeChangeComplete', () => {
            //@ts-ignore
            window.OneTrust.changeLanguage(newLocale);
          });
      },
      push: ({ url, as, options }) => router.push(url, as, options),
      reload: () => router.reload(),
    };
  }, [router, query]);

  return (
    <RouterContext.Provider value={contextValue}>
      {children}
    </RouterContext.Provider>
  );
};
